/**
 * Integration Constants
 * Shared metadata for all integrations across frontend and backend
 */
import { ActionSubtype } from '../models/workflow-dtos.js';
/**
 * Integration category enum
 */
export var IntegrationCategory;
(function (IntegrationCategory) {
    IntegrationCategory["LISTINGS"] = "LISTINGS";
    IntegrationCategory["CONTACTS"] = "CONTACTS";
    IntegrationCategory["PROPERTIES"] = "PROPERTIES";
    IntegrationCategory["PUSH_EVENTS"] = "PUSH_EVENTS";
    // Integration supports a per-staff free-text push assignee code (SUP-2305),
    // surfaced as an optional field on the Staff form when active.
    IntegrationCategory["STAFF_ASSIGNEE"] = "STAFF_ASSIGNEE";
})(IntegrationCategory || (IntegrationCategory = {}));
export const INTEGRATION_SYNC_TYPE_LABELS = {
    CONTACTS: 'Contacts',
    PROPERTIES: 'Properties',
    LISTINGS: 'Listings',
    UNITS: 'Units',
};
/**
 * Safely extract metadata as a plain object, defaulting to {} if null, non-object, or array.
 */
export function parseMetadataAsObject(metadata) {
    return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
        ? metadata
        : {};
}
/**
 * An Entrata configId is the portal subdomain (https://<configId>.entrata.com).
 * The cloud functions open that URL and type the stored credentials there,
 * so the value must be a plain subdomain that cannot point to another host.
 */
export const ENTRATA_CONFIG_ID_PATTERN = /^[a-z0-9-]+$/;
/**
 * True when the value is a valid Entrata configId (lowercase letters, digits, hyphens).
 */
export function isValidEntrataConfigId(value) {
    return typeof value === 'string' && ENTRATA_CONFIG_ID_PATTERN.test(value);
}
/**
 * Fields that non-admin users are allowed to update in metadata-only integrations.
 * Admin-set fields (e.g., configId) are excluded to prevent overwrites.
 */
export const ALLOWED_METADATA_FIELDS = {
    RENT_MANAGER: ['defaultStatusId'],
};
/**
 * Validates and extracts only allowlisted metadata fields for the given integration type.
 * Pure function — returns sanitized fields and any disallowed keys. Caller decides how to handle disallowed fields.
 */
export function sanitizeMetadata(integrationType, incoming) {
    const allowed = ALLOWED_METADATA_FIELDS[integrationType] || [];
    const allowedSet = new Set(allowed);
    const disallowed = Object.keys(incoming).filter(k => !allowedSet.has(k));
    const sanitized = {};
    for (const key of allowed) {
        if (key in incoming) {
            sanitized[key] = incoming[key];
        }
    }
    return { sanitized, disallowed };
}
/**
 * Integration category labels for UI display
 */
export const INTEGRATION_CATEGORY_LABELS = {
    [IntegrationCategory.LISTINGS]: 'Listings',
    [IntegrationCategory.CONTACTS]: 'Contacts',
    [IntegrationCategory.PROPERTIES]: 'Properties',
    [IntegrationCategory.PUSH_EVENTS]: 'Push Events',
    [IntegrationCategory.STAFF_ASSIGNEE]: 'Staff Assignee',
};
/**
 * Integration authentication type enum
 */
export var IntegrationAuthType;
(function (IntegrationAuthType) {
    IntegrationAuthType["API_KEY_CREDENTIAL"] = "API_KEY_CREDENTIAL";
    IntegrationAuthType["ACCOUNT_CREDENTIAL"] = "ACCOUNT_CREDENTIAL";
    IntegrationAuthType["METADATA_ONLY"] = "METADATA_ONLY";
})(IntegrationAuthType || (IntegrationAuthType = {}));
/**
 * OAuth callback query parameters
 */
export const INTEGRATION_CALLBACK_PARAMS = {
    SUCCESS: {
        BUILDIUM_CONNECTED: 'buildium_connected',
        PROPERTY_MELD_CONNECTED: 'property_meld_connected',
        HUBSPOT_CONNECTED: 'hubspot_connected',
    },
    ERROR: {
        NO_TOKEN: 'no_token',
        NO_STATE: 'no_state',
        CONNECTION_FAILED: 'connection_failed',
    },
};
export const INTEGRATIONS = {
    APPFOLIO: {
        // Core identity
        TYPE: 'APPFOLIO',
        NAME: 'AppFolio',
        URL: 'appfolio',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Property management software for residential and commercial properties',
        CATEGORIES: [
            IntegrationCategory.CONTACTS,
            IntegrationCategory.PROPERTIES,
            IntegrationCategory.PUSH_EVENTS,
        ],
        LOGO_PATH: '/assets/images/appfolio-logo.png',
        HELP_TEXT: 'AppFolio does not expose a public API. Export owners or tenants to CSV and drop the file in — we map the columns for you.',
        FEATURED_ORDER: 20,
        // OTP Configuration for 2FA
        OTP: {
            ENABLED: true,
            PATTERN: /^(\d{6})\s+Use this code to verify your AppFolio account\.\s+This code will expire in \d+ minutes\./,
            CODE_GROUP: 1,
            EXPIRY_MINUTES: 10,
        },
        // Push action subtypes - multiple push types available
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.SERVICE_REQUEST,
                name: 'Service Request',
                eventType: 'job.appfolio-service-request-push.initiated',
            },
            {
                type: ActionSubtype.GUEST_CARD,
                name: 'Guest Card',
                eventType: 'job.appfolio-guest-card-push.initiated',
            },
        ],
    },
    BUILDING_LINK: {
        // Core identity
        TYPE: 'BUILDING_LINK',
        NAME: 'BuildingLink',
        URL: 'building-link',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Not active — the work-order push has no cloud function, no Pub/Sub
        // subscription and no PUSH_INTEGRATION_REGISTRY entry, so a dispatch
        // publishes to a topic nothing reads and the push is lost without an
        // error. The definition stays: the database enums and the credential
        // decrypt path still refer to this type.
        HIDDEN: true,
        // Frontend display metadata
        DESCRIPTION: 'Property management software for residential communities',
        CATEGORIES: [IntegrationCategory.PUSH_EVENTS],
        LOGO_PATH: '/assets/images/building-link-logo.png',
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.building-link-work-order-push.initiated',
            },
        ],
    },
    BUILDIUM: {
        // Core identity
        TYPE: 'BUILDIUM',
        NAME: 'Buildium',
        URL: 'buildium',
        AUTH_TYPE: IntegrationAuthType.API_KEY_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Cloud-based property management software for property managers and real estate professionals',
        CATEGORIES: [
            IntegrationCategory.LISTINGS,
            IntegrationCategory.CONTACTS,
            IntegrationCategory.PROPERTIES,
            IntegrationCategory.PUSH_EVENTS,
        ],
        LOGO_PATH: '/assets/images/buildium-logo.svg',
        HELP_TEXT: 'Buildium → Settings → API Keys. Needs read and write on work orders.',
        FEATURED_ORDER: 10,
        // Sync configuration
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['CONTACTS', 'PROPERTIES', 'LISTINGS', 'UNITS'],
        DEFAULT_SYNC_INTERVAL: 12 * 60 * 60 * 1000, // Every 12 hours in milliseconds
        // Backend API configuration
        API_ENDPOINTS: {
            // Credentials
            CREDENTIALS_EXCHANGE: '/v1/credentials',
            // Rental endpoints
            RENTAL_UNIT_LISTINGS: '/v1/rentals/units/listings',
            RENTAL_OWNERS: '/v1/rentals/owners',
            RENTAL_TENANTS: '/v1/leases/tenants',
            RENTAL_PROPERTIES: '/v1/rentals',
            // Association endpoints
            ASSOCIATION: '/v1/associations',
            ASSOCIATION_OWNERS: '/v1/associations/owners',
            ASSOCIATION_TENANTS: '/v1/associations/tenants',
            // Unit endpoints
            RENTAL_UNITS: '/v1/rentals/units',
            ASSOCIATION_UNITS: '/v1/associations/units',
            // Work orders
            WORK_ORDERS: '/v1/workorders',
            // Task endpoints
            RESIDENT_REQUESTS: '/v1/tasks/residentrequests',
            RENTAL_OWNER_REQUESTS: '/v1/tasks/rentalownerrequests',
            CONTACT_REQUESTS: '/v1/tasks/contactrequests',
            // Utility endpoints
            TASK_CATEGORIES: '/v1/tasks/categories',
            USERS: '/v1/users',
            VENDORS: '/v1/vendors',
            RESIDENT_CENTER_USERS: '/v1/residentCenterUsers',
        },
        // External ID prefix for associations (to avoid collision with rental IDs)
        ASSOCIATION_EXTERNAL_ID_PREFIX: 'association-',
        HEADERS: {
            CLIENT_ID: 'x-buildium-client-id',
            CLIENT_SECRET: 'x-buildium-client-secret',
        },
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.buildium-work-order-push.initiated',
                handoffFields: [
                    {
                        key: 'assigneeId',
                        label: 'Assignee',
                        metadataKey: 'defaultAssignedToUserId',
                        type: 'numeric',
                        inputType: 'dropdown',
                        dropdownSource: 'buildium-staff-users',
                    },
                    {
                        key: 'vendorId',
                        label: 'Vendor',
                        metadataKey: 'defaultVendorId',
                        type: 'numeric',
                        inputType: 'dropdown',
                        dropdownSource: 'buildium-vendors',
                    },
                ],
            },
            {
                type: ActionSubtype.TASK,
                name: 'Task',
                eventType: 'job.buildium-task-push.initiated',
                variants: [
                    {
                        id: 'resident_request',
                        name: 'Resident Request',
                        handoffFields: [
                            {
                                key: 'assigneeId',
                                label: 'Assignee',
                                metadataKey: 'defaultResidentAssigneeId',
                                type: 'numeric',
                                inputType: 'dropdown',
                                dropdownSource: 'buildium-staff-users',
                            },
                            {
                                key: 'categoryId',
                                label: 'Category',
                                type: 'numeric',
                                description: 'Optional. If empty, AI will select the best matching category.',
                                inputType: 'dropdown',
                                dropdownSource: 'buildium-task-categories',
                            },
                        ],
                    },
                    {
                        id: 'rental_owner_request',
                        name: 'Rental Owner Request',
                        handoffFields: [
                            {
                                key: 'assigneeId',
                                label: 'Assignee',
                                metadataKey: 'defaultRentalOwnerAssigneeId',
                                type: 'numeric',
                                inputType: 'dropdown',
                                dropdownSource: 'buildium-staff-users',
                            },
                            {
                                key: 'categoryId',
                                label: 'Category',
                                type: 'numeric',
                                description: 'Optional. If empty, AI will select the best matching category.',
                                inputType: 'dropdown',
                                dropdownSource: 'buildium-task-categories',
                            },
                        ],
                    },
                    {
                        id: 'contact_request',
                        name: 'Contact Request',
                        handoffFields: [
                            {
                                key: 'assigneeId',
                                label: 'Assignee',
                                metadataKey: 'defaultContactAssigneeId',
                                type: 'numeric',
                                inputType: 'dropdown',
                                dropdownSource: 'buildium-staff-users',
                            },
                            {
                                key: 'categoryId',
                                label: 'Category',
                                type: 'numeric',
                                description: 'Optional. If empty, AI will select the best matching category.',
                                inputType: 'dropdown',
                                dropdownSource: 'buildium-task-categories',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    PROPERTY_MELD: {
        // Core identity
        TYPE: 'PROPERTY_MELD',
        NAME: 'Property Meld',
        URL: 'property-meld',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Maintenance coordination platform for property managers and maintenance teams',
        CATEGORIES: [IntegrationCategory.PUSH_EVENTS],
        LOGO_PATH: '/assets/images/property-meld.jpeg',
        HELP_TEXT: 'Property Meld → Account → Integrations → Generate API key.',
        FEATURED_ORDER: 30,
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.property-meld-push.initiated',
            },
        ],
    },
    YARDI_VOYAGER: {
        // Core identity
        TYPE: 'YARDI_VOYAGER',
        NAME: 'Yardi Voyager',
        URL: 'yardi-voyager',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Property management and accounting software for residential and commercial real estate',
        CATEGORIES: [
            IntegrationCategory.PUSH_EVENTS,
            IntegrationCategory.STAFF_ASSIGNEE,
        ],
        LOGO_PATH: '/assets/images/yardi-voyager-logo.png',
        // OTP Configuration for 2FA
        OTP: {
            ENABLED: true,
            // Anchor only the stable prefix — Yardi's tail copy and colon usage change over time
            PATTERN: /Your YardiOne code is:?\s*(\d{6})\b/,
            CODE_GROUP: 1,
            EXPIRY_MINUTES: 5,
        },
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.yardi-voyager-work-order-push.initiated',
                handoffFields: [
                    {
                        // No metadataKey: pre-filling would shadow directoryAssignee
                        // on-call resolution. The metadata value is still the mapper's
                        // last-resort fallback, so it's declared as defaultMetadataKey for
                        // the preview to report — see resolvePushAssignee in the
                        // yardi-voyager-workorder-push mapper.
                        key: 'laborEmployee',
                        label: 'Labor Employee',
                        type: 'text',
                        directoryAssignee: true,
                        defaultMetadataKey: 'laborEmployee',
                        placeholder: 'e.g. John Smith or 12345',
                        description: 'Employee name or code to assign on the Labor tab (e.g., "John Smith" or 12345)',
                    },
                ],
            },
            {
                type: ActionSubtype.GUEST_CARD,
                name: 'Guest Card',
                eventType: 'job.yardi-voyager-guest-card-push.initiated',
            },
        ],
    },
    YARDI_BREEZE: {
        // Core identity
        TYPE: 'YARDI_BREEZE',
        NAME: 'Yardi Breeze',
        URL: 'yardi-breeze',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Easy-to-use property management software for small to mid-sized portfolios',
        CATEGORIES: [IntegrationCategory.PUSH_EVENTS],
        LOGO_PATH: '/assets/images/yardi-breeze-logo.png',
        // OTP Configuration for 2FA
        OTP: {
            ENABLED: true,
            // Anchor only the stable prefix — Yardi's tail copy and colon usage change over time
            PATTERN: /Your YardiOne code is:?\s*(\d{6})\b/,
            CODE_GROUP: 1,
            EXPIRY_MINUTES: 5,
        },
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.yardi-breeze-work-order-push.initiated',
            },
            {
                type: ActionSubtype.GUEST_CARD,
                name: 'Guest Card',
                eventType: 'job.yardi-breeze-guest-card-push.initiated',
            },
        ],
    },
    ENTRATA: {
        // Core identity
        TYPE: 'ENTRATA',
        NAME: 'Entrata',
        URL: 'entrata',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Property management software for multifamily residential portfolios',
        CATEGORIES: [IntegrationCategory.PUSH_EVENTS],
        LOGO_PATH: '/assets/images/entrata-logo.png',
        // OTP Configuration for 2FA. Entrata sends the code by email.
        OTP: {
            ENABLED: true,
            // The OTP line is generic copy, so the pattern also needs "Entrata" later in the body
            PATTERN: /Your one-time password \(OTP\) is:?\s*(\d{6})\b[\s\S]*\bEntrata\b/,
            CODE_GROUP: 1,
            EXPIRY_MINUTES: 10,
        },
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.entrata-work-order-push.initiated',
            },
            {
                type: ActionSubtype.GUEST_CARD,
                name: 'Guest Card',
                eventType: 'job.entrata-guest-card-push.initiated',
            },
        ],
    },
    EPIC_PM: {
        // Core identity
        TYPE: 'EPIC_PM',
        NAME: 'Epic PM',
        URL: 'epic-pm',
        AUTH_TYPE: IntegrationAuthType.ACCOUNT_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Property management software for Epic PM',
        CATEGORIES: [IntegrationCategory.PUSH_EVENTS],
        LOGO_PATH: '/assets/images/epic-pm-logo.png',
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.epic-pm-work-order-push.initiated',
            },
        ],
    },
    DOORLOOP: {
        // Core identity
        TYPE: 'DOORLOOP',
        NAME: 'DoorLoop',
        URL: 'doorloop',
        AUTH_TYPE: IntegrationAuthType.API_KEY_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Property management software for residential, commercial, and community associations',
        CATEGORIES: [
            IntegrationCategory.CONTACTS,
            IntegrationCategory.PROPERTIES,
            IntegrationCategory.PUSH_EVENTS,
            IntegrationCategory.LISTINGS,
        ],
        LOGO_PATH: '/assets/images/door-loop.png',
        HELP_TEXT: 'DoorLoop → Settings → API. Generate a key with read + write scope on work orders and tasks.',
        // Vendors that expose only a single credential ("API key") map it to
        // clientSecret on the shared api-key-credentials endpoint. clientId is
        // required by the DTO, so we provide a stable per-vendor sentinel here so
        // the shared connector doesn't have to hardcode vendor names.
        CLIENT_ID_SENTINEL: 'doorloop',
        // Sync configuration — contacts (tenants + owners), properties, units,
        // listings. Leases are consumed inside contact resolution (tenant→
        // property/unit links), not persisted as a standalone sync type. Listings
        // have no dedicated endpoint — they ride on GET /units as a `listing`
        // subobject, populated only for units listed via DoorLoop's Listings
        // feature.
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['CONTACTS', 'PROPERTIES', 'UNITS', 'LISTINGS'],
        DEFAULT_SYNC_INTERVAL: 12 * 60 * 60 * 1000, // Every 12 hours in milliseconds
        // Backend API configuration — fixed base URL, no subdomain.
        // Every entry must be a GET-able list endpoint: connect-time validation
        // derives its probes from this map (GET <path>?page_size=1).
        API_ENDPOINTS: {
            TENANTS: '/tenants',
            OWNERS: '/owners',
            PROPERTIES: '/properties',
            UNITS: '/units',
            LEASES: '/leases',
            USERS: '/users',
            VENDORS: '/vendors',
            TASKS: '/tasks',
        },
        // Push action subtypes. The variant picks the DoorLoop task type and its
        // required party: internal tasks need a requested-by user, work orders
        // need an assigned vendor. Tenant/owner requests (which require the
        // caller's synced DoorLoop id) are a later addition.
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.TASK,
                name: 'Task',
                eventType: 'job.doorloop-task-push.initiated',
                variants: [
                    {
                        id: 'internal_task',
                        name: 'Internal Task',
                        handoffFields: [
                            {
                                key: 'assigneeId',
                                label: 'Assignee',
                                metadataKey: 'defaultTaskAssigneeId',
                                type: 'text', // DoorLoop ids are Mongo ObjectId strings
                                inputType: 'dropdown',
                                dropdownSource: 'doorloop-users',
                            },
                            {
                                key: 'requestedByUserId',
                                label: 'Requested by',
                                metadataKey: 'defaultRequestedByUserId',
                                type: 'text',
                                description: 'Recorded in DoorLoop as who requested the task. Required for internal tasks.',
                                inputType: 'dropdown',
                                dropdownSource: 'doorloop-users',
                            },
                        ],
                    },
                    {
                        id: 'work_order',
                        name: 'Work Order',
                        handoffFields: [
                            {
                                key: 'assigneeId',
                                label: 'Assignee',
                                metadataKey: 'defaultTaskAssigneeId',
                                type: 'text',
                                inputType: 'dropdown',
                                dropdownSource: 'doorloop-users',
                            },
                            {
                                key: 'vendorId',
                                label: 'Vendor',
                                metadataKey: 'defaultVendorId',
                                type: 'text',
                                description: 'Vendor the work order is assigned to. Required for work orders.',
                                inputType: 'dropdown',
                                dropdownSource: 'doorloop-vendors',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    RENTVINE: {
        // Core identity
        TYPE: 'RENTVINE',
        NAME: 'Rentvine',
        URL: 'rentvine',
        AUTH_TYPE: IntegrationAuthType.API_KEY_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Property management software for residential and commercial properties',
        CATEGORIES: [
            IntegrationCategory.LISTINGS,
            IntegrationCategory.CONTACTS,
            IntegrationCategory.PROPERTIES,
            IntegrationCategory.PUSH_EVENTS,
        ],
        LOGO_PATH: '/assets/images/rent-vine.png',
        // Sync configuration
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['CONTACTS', 'PROPERTIES', 'UNITS', 'LISTINGS'],
        DEFAULT_SYNC_INTERVAL: 12 * 60 * 60 * 1000, // Every 12 hours in milliseconds
        // Backend API configuration
        API_ENDPOINTS: {
            TENANTS_SEARCH: '/tenants/search',
            TENANT_DETAILS: '/tenants/:contactID',
            OWNERS_SEARCH: '/owners/search',
            VENDORS_SEARCH: '/vendors/search',
            PORTFOLIOS: '/portfolios',
            PORTFOLIO_DETAILS: '/portfolios/:portfolioID',
            PROPERTIES: '/properties',
            PROPERTY_DETAILS: '/properties/:propertyID',
            PROPERTY_UNITS: '/properties/:propertyID/units',
            LISTINGS_SEARCH: '/properties/listings/search',
            WORK_ORDERS: '/maintenance/work-orders',
        },
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.rentvine-work-order-push.initiated',
            },
        ],
    },
    RENT_MANAGER: {
        // Core identity
        TYPE: 'RENT_MANAGER',
        NAME: 'Rent Manager',
        URL: 'rent-manager',
        AUTH_TYPE: IntegrationAuthType.METADATA_ONLY,
        // Frontend display metadata
        DESCRIPTION: 'Comprehensive property management solution for commercial and residential properties',
        CATEGORIES: [
            IntegrationCategory.LISTINGS,
            IntegrationCategory.CONTACTS,
            IntegrationCategory.PROPERTIES,
            IntegrationCategory.PUSH_EVENTS,
        ],
        LOGO_PATH: '/assets/images/rent-manager.jpeg',
        HELP_TEXT: 'Rent Manager → Tools → API Access. The key needs read and write on work orders.',
        // Sync configuration
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['CONTACTS', 'PROPERTIES', 'LISTINGS', 'UNITS'],
        DEFAULT_SYNC_INTERVAL: 12 * 60 * 60 * 1000, // Every 12 hours in milliseconds
        // Push action subtypes
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.WORK_ORDER,
                name: 'Work Order',
                eventType: 'job.rent-manager-push.initiated',
            },
        ],
    },
    SHOWMOJO: {
        // Core identity
        TYPE: 'SHOWMOJO',
        NAME: 'ShowMojo',
        URL: 'showmojo',
        AUTH_TYPE: IntegrationAuthType.API_KEY_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Automated leasing platform built for property managers',
        CATEGORIES: [IntegrationCategory.LISTINGS],
        LOGO_PATH: '/assets/images/show-mojo.png',
        CLIENT_ID_SENTINEL: 'SHOW_MOJO_DEFAULT',
        // Sync configuration
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['LISTINGS'],
        DEFAULT_SYNC_INTERVAL: 12 * 60 * 60 * 1000, // Every 12 hours in milliseconds
        // Backend API configuration
        API_ENDPOINTS: {
            LISTINGS: '/v3/listings',
            AVAILABLE_TIMES: '/v3/tours/:uid/available_times',
            SCREENING_QUESTIONS: '/v3/tours/:uid/screening_questions',
            SCHEDULE_TOUR: '/v3/tours/schedule',
            AI_CONVERSATIONS: '/ai/conversations',
        },
        HEADERS: {
            AUTHORIZATION: 'Authorization',
        },
    },
    TENANT_TURNER: {
        // Core identity
        TYPE: 'TENANT_TURNER',
        NAME: 'Tenant Turner',
        URL: 'tenant-turner',
        AUTH_TYPE: IntegrationAuthType.API_KEY_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'Tenant lead scheduling and showing software for property managers',
        CATEGORIES: [IntegrationCategory.LISTINGS],
        LOGO_PATH: '/assets/images/tenant-turner-logo.png',
        CLIENT_ID_SENTINEL: 'TENANT_TURNER_DEFAULT',
        // Sync configuration
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['LISTINGS'],
        DEFAULT_SYNC_INTERVAL: 6 * 60 * 60 * 1000, // Every 6 hours in milliseconds
        // Backend API configuration
        API_ENDPOINTS: {
            PROPERTIES: '/v1/properties',
            PROPERTY: '/v1/properties/:propertyId',
        },
        HEADERS: {
            AUTHORIZATION: 'Authorization',
        },
    },
    HUBSPOT: {
        // Core identity
        TYPE: 'HUBSPOT',
        NAME: 'HubSpot',
        URL: 'hubspot',
        AUTH_TYPE: IntegrationAuthType.API_KEY_CREDENTIAL,
        // Frontend display metadata
        DESCRIPTION: 'CRM platform for sales, marketing, and customer service teams',
        CATEGORIES: [IntegrationCategory.CONTACTS, IntegrationCategory.PUSH_EVENTS],
        LOGO_PATH: '/assets/images/hubspot-logo.svg',
        HELP_TEXT: "You'll sign in with HubSpot. We only ask for the scopes your agents need.",
        // Background sync configuration (contacts only for now)
        SUPPORTS_SYNC: true,
        SYNC_TYPES: ['CONTACTS'],
        DEFAULT_SYNC_INTERVAL: 12 * 60 * 60 * 1000, // Every 12 hours in milliseconds
        // OAuth 2.0 public-app configuration (defaults; overridable via env on the API)
        OAUTH: {
            AUTHORIZE_URL: 'https://app.hubspot.com/oauth/authorize',
            TOKEN_URL: 'https://api.hubapi.com/oauth/v3/token',
            // Scopes requested when a customer authorizes our app.
            // Must match the HubSpot app's requiredScopes exactly.
            // contacts.write is required to create tasks (the tasks API is gated
            // under the contacts scope family — there is no crm.objects.tasks.write).
            // It also covers the scheduler create-calendar-event write; only the
            // meeting-link/availability reads need the dedicated scheduler scope.
            // The tickets API is gated by the legacy `tickets` scope (read + write,
            // also covers ticket pipelines) — there is no crm.objects.tickets.*.
            SCOPES: [
                'oauth',
                'crm.objects.owners.read',
                'crm.objects.contacts.read',
                'crm.objects.contacts.write',
                'tickets',
                'scheduler.meetings.meeting-link.read',
            ],
        },
        // Backend API configuration (date-based API version 2026-03)
        API_BASE_URL: 'https://api.hubapi.com',
        API_ENDPOINTS: {
            // Task assignees are sourced from the owners API — an owner's `id` is the
            // value HubSpot tasks use for hubspot_owner_id.
            OWNERS: '/crm/owners/2026-03',
            // CRM contacts list (date-based API version).
            CONTACTS: '/crm/objects/2026-03/contacts',
            // CRM tasks (date-based API version). Used by the task push.
            TASKS: '/crm/objects/2026-03/tasks',
            // CRM tickets (date-based API version). Used by the ticket push.
            TICKETS: '/crm/objects/2026-03/tickets',
            // Ticket pipelines + stages (v3 pipelines API) for the admin dropdowns.
            TICKET_PIPELINES: '/crm/v3/pipelines/tickets',
            // Scheduler booking-page availability (read gated by
            // scheduler.meetings.meeting-link.read).
            MEETING_LINK_AVAILABILITY: '/scheduler/2026-03/meetings/meeting-links/book/availability-page/:slug',
            // Scheduler book: validates the slot against live availability, creates
            // or dedupes the contact by email, and sends the calendar invite
            // (write gated by crm.objects.contacts.write).
            MEETING_LINKS_BOOK: '/scheduler/2026-03/meetings/meeting-links/book',
        },
        // HUBSPOT_DEFINED association type ids used when associating CRM objects.
        ASSOCIATION_TYPE_IDS: {
            TASK_TO_CONTACT: 204,
            TICKET_TO_CONTACT: 16,
        },
        // Default hours added to the engagement time for a task's due date when the
        // integration has no taskDueDateOffsetHours configured (1 day).
        DEFAULT_TASK_DUE_DATE_OFFSET_HOURS: 24,
        // Push action subtypes. The task owner is the handoff-level assignee
        // (actionParameters.assigneeId) when set, otherwise the integration default
        // (metadata.defaultTaskAssigneeId).
        ACTION_SUBTYPES: [
            {
                type: ActionSubtype.TASK,
                name: 'Task',
                eventType: 'job.hubspot-task-push.initiated',
                handoffFields: [
                    {
                        key: 'assigneeId',
                        label: 'Assignee',
                        metadataKey: 'defaultTaskAssigneeId',
                        type: 'text', // HubSpot owner ids are strings
                        inputType: 'dropdown',
                        dropdownSource: 'hubspot-owners',
                    },
                ],
            },
            {
                type: ActionSubtype.TICKET,
                name: 'Ticket',
                eventType: 'job.hubspot-ticket-push.initiated',
                // Each field: handoff-level override (actionParameters) falling back to
                // the integration default (metadata.defaultTicket*). Pipeline + stage
                // are required to create a ticket.
                handoffFields: [
                    {
                        key: 'pipelineId',
                        label: 'Pipeline',
                        metadataKey: 'defaultTicketPipelineId',
                        type: 'text',
                        inputType: 'dropdown',
                        dropdownSource: 'hubspot-ticket-pipelines',
                    },
                    {
                        key: 'stageId',
                        label: 'Stage',
                        metadataKey: 'defaultTicketStageId',
                        type: 'text',
                        inputType: 'dropdown',
                        dropdownSource: 'hubspot-ticket-stages',
                        dependsOnField: 'pipelineId', // stages cascade from the chosen pipeline
                    },
                    {
                        key: 'ownerId',
                        label: 'Owner',
                        metadataKey: 'defaultTicketOwnerId',
                        type: 'text',
                        inputType: 'dropdown',
                        dropdownSource: 'hubspot-owners',
                    },
                ],
            },
        ],
    },
};
export const ALL_INTEGRATIONS = Object.values(INTEGRATIONS);
/**
 * The integrations offered to users, excluding entries marked HIDDEN.
 * Use when listing integrations to pick from. Use ALL_INTEGRATIONS to resolve
 * a stored type, so a hidden integration's existing records stay readable.
 */
export const VISIBLE_INTEGRATIONS = ALL_INTEGRATIONS.filter(integration => !('HIDDEN' in integration && integration.HIDDEN));
/**
 * Valid integration type values for validation (e.g. @IsIn).
 * Use when validating Partner.integrationType or other IntegrationType fields.
 */
export const INTEGRATION_TYPE_VALUES = ALL_INTEGRATIONS.map(i => i.TYPE);
/**
 * Valid integration sync type values for validation (e.g. @IsIn).
 */
export const INTEGRATION_SYNC_TYPE_VALUES = [
    'CONTACTS',
    'PROPERTIES',
    'LISTINGS',
    'UNITS',
];
function hasSync(integration) {
    return integration.SUPPORTS_SYNC === true;
}
/**
 * The handoff field marked `directoryAssignee` for an integration (e.g. Yardi
 * Voyager's work-order `laborEmployee`), or undefined when it has none. Its
 * `label`/`description` drive the Staff-form field copy so guidance stays
 * per-integration correct; its `key` drives dispatch-time resolution.
 */
export function getDirectoryAssigneeField(integrationType) {
    const integration = ALL_INTEGRATIONS.find(i => i.TYPE === integrationType);
    for (const subtype of integration?.ACTION_SUBTYPES ?? []) {
        const fields = [
            ...(subtype.handoffFields ?? []),
            ...(subtype.variants?.flatMap(variant => variant.handoffFields ?? []) ??
                []),
        ];
        const assigneeField = fields.find(field => field.directoryAssignee);
        if (assigneeField)
            return assigneeField;
    }
    return undefined;
}
/**
 * The actionParameters key a directory-based push assignee resolves into for an
 * integration. Undefined when the integration has no marked assignee field.
 */
export function getDirectoryAssigneeParamKey(integrationType) {
    return getDirectoryAssigneeField(integrationType)?.key;
}
/**
 * The actionParameters key holding a DirectoryAssigneeRef, kept deliberately
 * separate from the literal-value key above: integration mappers read only the
 * literal key, so a reference can never reach the integration verbatim even if
 * a resolution step is missed. A literal value set on the same action still
 * wins over the reference.
 */
export function getDirectoryAssigneeRefKey(integrationType) {
    const key = getDirectoryAssigneeParamKey(integrationType);
    return key ? directoryAssigneeRefKey(key) : undefined;
}
/**
 * The ref key paired with a given handoff field key. Shares the suffix with
 * getDirectoryAssigneeRefKey so the editor (which holds a field) and dispatch
 * (which holds an integration type) can never disagree about where the
 * reference lives.
 */
export function directoryAssigneeRefKey(fieldKey) {
    return `${fieldKey}__ref`;
}
/**
 * Reads a DirectoryAssigneeRef out of an actionParameters value, or undefined
 * when absent/malformed. Tolerant by design: actionParameters is untyped JSON
 * that predates this field, and a bad ref should degrade to "no reference"
 * rather than break a push.
 */
export function parseDirectoryAssigneeRef(value) {
    if (!value || typeof value !== 'object')
        return undefined;
    const { kind, id } = value;
    if (kind !== 'staff' && kind !== 'role')
        return undefined;
    if (typeof id !== 'string' || id.length === 0)
        return undefined;
    return { kind, id };
}
/**
 * Integration types that support a per-staff push assignee field, used to gate
 * the optional assignee field on the Staff form. The `STAFF_ASSIGNEE` category
 * drives this FE listing; the `directoryAssignee` handoff-field marker (see
 * getDirectoryAssigneeParamKey) drives dispatch. The two are kept in lockstep
 * by a consistency test so neither can be declared without the other.
 */
export function getStaffAssigneeEnabledIntegrationTypes() {
    return ALL_INTEGRATIONS.filter(integration => integration.CATEGORIES.includes(IntegrationCategory.STAFF_ASSIGNEE)).map(integration => integration.TYPE);
}
/**
 * Check if an integration type supports background sync
 * Used to show/hide manual sync button in UI
 */
export function integrationSupportsSync(integrationType) {
    const integration = ALL_INTEGRATIONS.find(i => i.TYPE === integrationType);
    if (!integration) {
        return false;
    }
    return hasSync(integration);
}
/**
 * Get sync configuration for an integration
 */
export function getIntegrationSyncConfig(integrationType) {
    const integration = ALL_INTEGRATIONS.find(i => i.TYPE === integrationType);
    if (!integration || !hasSync(integration)) {
        return null;
    }
    return {
        supportsSync: true,
        syncTypes: integration.SYNC_TYPES,
        defaultInterval: integration.DEFAULT_SYNC_INTERVAL,
    };
}
/**
 * Detect OTP from message content across all enabled integrations
 * Returns all matching integration types (some integrations share OTP patterns)
 */
export function detectOtpFromContent(content) {
    const matches = [];
    for (const integration of ALL_INTEGRATIONS) {
        const otp = integration.OTP;
        if (!otp?.ENABLED)
            continue;
        const match = content.match(otp.PATTERN);
        if (match) {
            matches.push({
                integrationType: integration.TYPE,
                code: match[otp.CODE_GROUP],
                expiryMinutes: otp.EXPIRY_MINUTES,
            });
        }
    }
    if (matches.length === 0) {
        return { detected: false };
    }
    // Return first match for backward compatibility, but include all matches
    const firstMatch = matches[0];
    return {
        detected: true,
        integrationType: firstMatch.integrationType,
        matchingIntegrationTypes: matches.map(m => m.integrationType),
        code: firstMatch.code,
        expiryMinutes: firstMatch.expiryMinutes,
    };
}
//# sourceMappingURL=integrations.js.map